<script setup lang="ts">
import { ref } from 'vue';
import { useDisplay } from 'vuetify';
import { VDialog } from 'vuetify/components/VDialog';
import { VMenu } from 'vuetify/components/VMenu';

type Props = {
  activator?: HTMLElement | 'parent';
  location?: 'start' | 'end' | 'top' | 'bottom';
  titleDivider?: boolean;
  infoIcon?: boolean;
  header?: boolean;
  footer?: boolean;
  maxWidth?: number | string;
  hover?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  location: 'bottom',
  maxWidth: '520px',
});

const show = ref(false);

const { smAndDown } = useDisplay();
</script>

<template>
  <component
    :is="smAndDown ? VDialog : VMenu"
    :activator="props.activator"
    :close-on-content-click="false"
    :scrim="false"
    :content-class="smAndDown ? 'responsive' : ''"
    :location="`${props.location} center`"
    v-model="show"
    :open-on-click="!props.hover || smAndDown"
    :open-on-hover="props.hover && !smAndDown"
    :transition="smAndDown ? 'slide-y-reverse-transition' : undefined"
    :open-delay="100"
    :close-delay="100"
    :offset="20"
  >
    <!-- Activator -->
    <template v-if="!props.activator" #activator="slotProps">
      <slot name="activator" :isActive="slotProps.isActive" :props="slotProps.props"></slot>
    </template>

    <v-card
      class="mx-n6 my-n2 elevation-8"
      :class="smAndDown ? 'rounded-t-xl' : 'rounded-sm'"
      :max-width="smAndDown ? undefined : props.maxWidth"
    >
      <!-- Header -->
      <div v-if="props.header" class="h-14 flex items-center justify-between px-4">
        <div class="flex items-center text-subtitle-1 gap-2">
          <v-icon v-if="props.infoIcon" icon="fat fa-circle-info" class="text-icon-3"></v-icon>
          <slot name="title"></slot>
        </div>
        <v-icon
          v-if="!props.hover || smAndDown"
          icon="fat fa-xmark"
          @click.stop="() => (show = false)"
        ></v-icon>
      </div>

      <v-divider v-if="props.titleDivider"></v-divider>

      <!-- Content -->
      <div class="pa-4">
        <slot name="content"></slot>
      </div>

      <!-- Footer -->
      <div v-if="props.footer">
        <v-divider></v-divider>

        <div class="pa-4">
          <slot name="footer"></slot>
        </div>
      </div>
    </v-card>
  </component>
</template>

<style scoped lang="scss">
:deep(.responsive) {
  bottom: 0;
  width: 100%;
  margin: 0;
}
</style>
